import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LogService } from './log.service';

interface VersionInfo {
  version: string;
  buildHash: string;
  buildTime: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionService {

  public newVersionAvailable = new BehaviorSubject<boolean>(false);

  private currentVersion: VersionInfo | null = null;
  private versionCheckUrl = '/assets/version.json';
  private checkSubscription: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private logService: LogService
  ) {
    this.startVersionCheck();
  }

  public applyUpdate() {
    this.logService.log('Applying update, reloading');
    window.location.reload();
  }

  private startVersionCheck() {
    const checkInterval = environment.production === false ? 1 * 60 * 1000 : 6 * 60 * 60 * 1000;
    this.logService.log(`Starting version check every ${checkInterval/60000} minute(s)`);
    this.checkSubscription = interval(checkInterval)
      .pipe(
        switchMap(() =>
          this.http.get<VersionInfo>(`${this.versionCheckUrl}?t=${Date.now()}`, {
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          })
        ),
        catchError(err => {
          console.error('Version check failed:', err);
          return [];
        })
      )
      .subscribe(serverVersion => {
        this.logService.log(`Fetched version: ${JSON.stringify(serverVersion, null, 4)}`);
        if (!this.currentVersion) {
          this.currentVersion = serverVersion;
        } else if (serverVersion.buildHash !== this.currentVersion.buildHash) {
          this.logService.log('New version detected:', serverVersion);
          this.newVersionAvailable.next(true);
          this.stopVersionCheck(); // Pause checks until refresh
        }
      });
  }

  private stopVersionCheck() {
    if (this.checkSubscription) {
      this.logService.log('Stopping version checks');
      this.checkSubscription.unsubscribe();
      this.checkSubscription = null;
    }
  }

}
